/**
 * Kenzu Process
 * Shopify 2.0
 */

(() => {
  "use strict";

  const SECTION_SELECTOR = "[data-kenzu-process]";
  const instances = new WeakMap();

  class KenzuProcess {
    constructor(section) {
      this.section = section;

      this.hostedVideos = Array.from(
        section.querySelectorAll("video")
      );

      this.externalVideos = Array.from(
        section.querySelectorAll(
          "[data-process-external-video]"
        )
      );

      this.abortController = new AbortController();
      this.revealObserver = null;
      this.videoObserver = null;
      this.externalObservers = [];

      this.init();
    }

    init() {
      this.initializeReveal();
      this.initializeHostedVideos();
      this.initializeExternalVideos();

      document.addEventListener(
        "visibilitychange",
        () => this.handleVisibilityChange(),
        {
          signal: this.abortController.signal
        }
      );
    }

    initializeReveal() {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (
        reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        this.section.classList.add("is-visible");
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.section.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: "0px 0px -8% 0px"
        }
      );

      this.revealObserver.observe(this.section);
    }

    initializeHostedVideos() {
      if (!this.hostedVideos.length) return;

      this.hostedVideos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        if (!video.hasAttribute("preload")) {
          video.preload = "metadata";
        }
      });

      if (!("IntersectionObserver" in window)) {
        this.hostedVideos.forEach((video) => {
          if (video.autoplay) {
            this.safePlay(video);
          }
        });

        return;
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            if (!(video instanceof HTMLVideoElement)) {
              return;
            }

            if (
              entry.isIntersecting &&
              video.autoplay &&
              !document.hidden
            ) {
              this.safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: "120px 0px"
        }
      );

      this.hostedVideos.forEach((video) => {
        this.videoObserver.observe(video);
      });
    }

    initializeExternalVideos() {
      this.externalVideos.forEach((container) => {
        const playButton = container.querySelector(
          "[data-process-play]"
        );

        const autoplay =
          container.dataset.autoplay === "true";

        playButton?.addEventListener(
          "click",
          () => {
            this.loadExternalVideo(container, true);
          },
          {
            signal: this.abortController.signal
          }
        );

        if (autoplay) {
          this.observeExternalVideo(container);
        }
      });
    }

    observeExternalVideo(container) {
      if (!("IntersectionObserver" in window)) {
        this.loadExternalVideo(container, true);
        return;
      }

      const observer = new IntersectionObserver(
        (entries, currentObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.loadExternalVideo(container, true);
            currentObserver.disconnect();
          });
        },
        {
          threshold: 0,
          rootMargin: "280px 0px"
        }
      );

      observer.observe(container);
      this.externalObservers.push(observer);
    }

    loadExternalVideo(container, autoplay = false) {
      if (!(container instanceof HTMLElement)) return;

      if (container.dataset.loaded === "true") {
        this.playExternalVideo(container);
        return;
      }

      const type = container.dataset.videoType;
      const id = container.dataset.videoId;
      const loop = container.dataset.loop === "true";
      const controls =
        container.dataset.controls === "true";

      if (!type || !id) return;

      const iframe = document.createElement("iframe");

      iframe.className = "kenzu-process__external-iframe";
      iframe.title = "Vídeo del proceso de Kenzu";
      iframe.loading = autoplay ? "lazy" : "eager";
      iframe.allow =
        "autoplay; encrypted-media; fullscreen; picture-in-picture";

      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");

      if (type === "youtube") {
        iframe.src = this.buildYouTubeUrl({
          id,
          autoplay,
          loop,
          controls
        });
      }

      if (type === "vimeo") {
        iframe.src = this.buildVimeoUrl({
          id,
          autoplay,
          loop,
          controls
        });
      }

      if (!iframe.src) return;

      iframe.addEventListener(
        "load",
        () => {
          container.classList.add("is-playing");
        },
        {
          once: true,
          signal: this.abortController.signal
        }
      );

      container.appendChild(iframe);
      container.dataset.loaded = "true";
      container.classList.add("is-playing");
    }

    buildYouTubeUrl({
      id,
      autoplay,
      loop,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        mute: "1",
        controls: controls ? "1" : "0",
        loop: loop ? "1" : "0",
        playsinline: "1",
        rel: "0",
        modestbranding: "1",
        enablejsapi: "1",
        origin: window.location.origin
      });

      if (loop) {
        params.set("playlist", id);
      }

      return `https://www.youtube.com/embed/${encodeURIComponent(
        id
      )}?${params.toString()}`;
    }

    buildVimeoUrl({
      id,
      autoplay,
      loop,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        muted: "1",
        loop: loop ? "1" : "0",
        controls: controls ? "1" : "0",
        playsinline: "1",
        autopause: "0",
        dnt: "1"
      });

      if (autoplay && !controls) {
        params.set("background", "1");
      }

      return `https://player.vimeo.com/video/${encodeURIComponent(
        id
      )}?${params.toString()}`;
    }

    playExternalVideo(container) {
      const iframe = container.querySelector("iframe");

      if (!(iframe instanceof HTMLIFrameElement)) return;

      const type = container.dataset.videoType;

      if (type === "youtube") {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "playVideo",
            args: []
          }),
          "*"
        );
      }

      if (type === "vimeo") {
        iframe.contentWindow?.postMessage(
          {
            method: "play"
          },
          "*"
        );
      }

      container.classList.add("is-playing");
    }

    pauseExternalVideo(container) {
      const iframe = container.querySelector("iframe");

      if (!(iframe instanceof HTMLIFrameElement)) return;

      const type = container.dataset.videoType;

      if (type === "youtube") {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: []
          }),
          "*"
        );
      }

      if (type === "vimeo") {
        iframe.contentWindow?.postMessage(
          {
            method: "pause"
          },
          "*"
        );
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAllVideos();
        return;
      }

      this.hostedVideos.forEach((video) => {
        if (
          video.autoplay &&
          this.isElementVisible(video)
        ) {
          this.safePlay(video);
        }
      });
    }

    pauseAllVideos() {
      this.hostedVideos.forEach((video) => {
        video.pause();
      });

      this.externalVideos.forEach((container) => {
        this.pauseExternalVideo(container);
      });
    }

    safePlay(video) {
      if (!(video instanceof HTMLVideoElement)) return;

      const playPromise = video.play();

      playPromise?.catch?.(() => {});
    }

    isElementVisible(element) {
      if (!(element instanceof Element)) return false;

      const rect = element.getBoundingClientRect();

      return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      );
    }

    destroy() {
      this.pauseAllVideos();

      this.abortController.abort();
      this.revealObserver?.disconnect();
      this.videoObserver?.disconnect();

      this.externalObservers.forEach((observer) => {
        observer.disconnect();
      });

      this.externalObservers = [];
    }
  }

  const initializeSection = (section) => {
    if (!(section instanceof HTMLElement)) return;
    if (instances.has(section)) return;

    instances.set(section, new KenzuProcess(section));
  };

  const initializeSections = (root = document) => {
    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initializeSection);
  };

  const destroySections = (root) => {
    const sections = [];

    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  };

  const boot = () => {
    initializeSections(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeSections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroySections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      const section =
        event.target.matches?.(SECTION_SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );

      section?.classList.add("is-visible");
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const step = event.target.closest?.(
        "[data-process-step]"
      );

      const section = step?.closest(
        SECTION_SELECTOR
      );

      section?.classList.add("is-visible");

      step?.scrollIntoView({
        behavior: window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
          ? "auto"
          : "smooth",
        block: "center"
      });
    }
  );
})();